import { listCustomSites, listDeletedSiteIds, replaceDeletedSiteIds } from '../state/siteIndex';
import { diagramStore } from '../state/diagramStore';
import { accountStorageKey, getAccountOwner, setAccountOwner } from './accountScope';
import { applyUserUiSettings, loadUserUiSettings } from './userSettings';
import { mergeWorkspaces } from './workspaceMerge';
import { stripPhotosFromDocument, type UserWorkspacePayload } from '@ukrop-nm/shared';

const STORAGE_SCOPE_RE = /^ukrop-nm\.u\.([^.]+)\./;

const ETALON = 'SITE-OFFICE-3F';
const CLOUD_VERSION_LIMIT = 3;

function compactBundle(
  bundle: { document: unknown; versions: unknown },
  opts?: { stripPhotos?: boolean; versionLimit?: number },
): { document: unknown; versions: unknown } {
  let document = bundle.document;
  if (opts?.stripPhotos) document = stripPhotosFromDocument(document);
  const limit = opts?.versionLimit ?? CLOUD_VERSION_LIMIT;
  const versions = Array.isArray(bundle.versions) ? bundle.versions.slice(0, Math.max(0, limit)) : [];
  const compactVersions = opts?.stripPhotos
    ? versions.map((item) => {
        if (!item || typeof item !== 'object') return item;
        const rec = item as { document?: unknown };
        return { ...rec, document: stripPhotosFromDocument(rec.document) };
      })
    : versions;
  return { document, versions: compactVersions };
}

/** Payload for Neon: custom sites only, no etalon, few versions. */
export function compactWorkspaceForCloud(
  payload: UserWorkspacePayload,
  opts?: { stripPhotos?: boolean; versionLimit?: number },
): UserWorkspacePayload {
  const diagrams: UserWorkspacePayload['diagrams'] = {};
  for (const [siteId, bundle] of Object.entries(payload.diagrams ?? {})) {
    if (siteId === ETALON || !bundle?.document) continue;
    diagrams[siteId] = compactBundle(bundle, opts);
  }
  const sites = Array.isArray(payload.sites)
    ? payload.sites.filter((s) => s && typeof s === 'object' && 'id' in s && (s as { id: string }).id !== ETALON)
    : [];
  return {
    ...payload,
    sites,
    diagrams,
    updatedAt: new Date().toISOString(),
  };
}

function overlayLiveDiagram(diagrams: UserWorkspacePayload['diagrams']) {
  try {
    const siteId = diagramStore.getSiteId();
    const document = diagramStore.getSnapshot();
    if (!siteId || siteId === ETALON || document.siteId !== siteId) return;
    const liveTs = typeof document.updatedAt === 'string' ? document.updatedAt : '';
    if (!liveTs) return;
    const stored = diagrams[siteId]?.document;
    const storedTs =
      stored && typeof stored === 'object' && 'updatedAt' in stored
        ? String((stored as { updatedAt?: unknown }).updatedAt ?? '')
        : '';
    if (storedTs && storedTs > liveTs) return;
    diagrams[siteId] = {
      document,
      versions: diagramStore.getVersions(),
    };
  } catch {
    /* store not ready in some tests */
  }
}

export function collectLocalWorkspace(): UserWorkspacePayload {
  const deletedSiteIds = listDeletedSiteIds();
  const deleted = new Set(deletedSiteIds);
  const sites = listCustomSites().filter((s) => !deleted.has(s.id));
  const ids = new Set<string>([...sites.map((s) => s.id)]);
  const diagrams: UserWorkspacePayload['diagrams'] = {};
  for (const id of ids) {
    if (deleted.has(id) || id === ETALON) continue;
    const docRaw = localStorage.getItem(accountStorageKey(`diagram.${id}`));
    if (!docRaw) continue;
    let document: unknown = null;
    let versions: unknown = [];
    try {
      document = JSON.parse(docRaw);
    } catch {
      continue;
    }
    const verRaw = localStorage.getItem(accountStorageKey(`diagram.versions.${id}`));
    if (verRaw) {
      try {
        versions = JSON.parse(verRaw);
      } catch {
        versions = [];
      }
    }
    diagrams[id] = { document, versions };
  }
  overlayLiveDiagram(diagrams);
  let drawingMeta: unknown = {};
  const metaRaw = localStorage.getItem(accountStorageKey('drawing-meta'));
  if (metaRaw) {
    try {
      drawingMeta = JSON.parse(metaRaw);
    } catch {
      drawingMeta = {};
    }
  }
  return {
    sites,
    diagrams,
    drawingMeta,
    settings: loadUserUiSettings(),
    deletedSiteIds,
    updatedAt: new Date().toISOString(),
  };
}

/** Read pre-login (guest) workspace keys — merged into account on sign-in. */
export function collectGuestWorkspace(): UserWorkspacePayload {
  return collectScopedWorkspace(null);
}

function listLocalOwnerIds(): Array<string | null> {
  const owners = new Set<string | null>([null]);
  const current = getAccountOwner();
  if (current) owners.add(current);
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    const match = key?.match(STORAGE_SCOPE_RE);
    if (match?.[1]) owners.add(match[1]);
  }
  return [...owners];
}

function collectScopedWorkspace(ownerId: string | null): UserWorkspacePayload {
  const owner = getAccountOwner();
  setAccountOwner(ownerId);
  try {
    return collectLocalWorkspace();
  } finally {
    setAccountOwner(owner);
  }
}

/** Merge every local scope (guest, current account, legacy account keys). */
export function collectAllLocalWorkspaces(): UserWorkspacePayload {
  let merged: UserWorkspacePayload = {
    sites: [],
    diagrams: {},
    drawingMeta: {},
    updatedAt: new Date().toISOString(),
  };
  for (const ownerId of listLocalOwnerIds()) {
    merged = mergeWorkspaces(merged, collectScopedWorkspace(ownerId));
  }
  return merged;
}

export function applyLocalWorkspace(payload: UserWorkspacePayload) {
  const deleted = new Set(
    (payload.deletedSiteIds ?? []).filter((id) => typeof id === 'string' && id !== ETALON),
  );
  replaceDeletedSiteIds([...deleted]);
  const sites = Array.isArray(payload.sites)
    ? payload.sites.filter((s) => s && typeof s === 'object' && 'id' in s && !deleted.has(String((s as { id: string }).id)))
    : [];
  localStorage.setItem(accountStorageKey('site-index'), JSON.stringify(sites));
  if (payload.drawingMeta && typeof payload.drawingMeta === 'object') {
    localStorage.setItem(accountStorageKey('drawing-meta'), JSON.stringify(payload.drawingMeta));
  }
  for (const id of deleted) {
    localStorage.removeItem(accountStorageKey(`diagram.${id}`));
    localStorage.removeItem(accountStorageKey(`diagram.versions.${id}`));
  }
  for (const [siteId, bundle] of Object.entries(payload.diagrams ?? {})) {
    if (deleted.has(siteId) || siteId === ETALON) continue;
    if (bundle?.document) {
      localStorage.setItem(accountStorageKey(`diagram.${siteId}`), JSON.stringify(bundle.document));
    }
    if (bundle?.versions) {
      localStorage.setItem(
        accountStorageKey(`diagram.versions.${siteId}`),
        JSON.stringify(bundle.versions),
      );
    }
  }
  listCustomSites();
  if (payload.settings) {
    applyUserUiSettings(payload.settings);
  }
}
